"use client";

import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { FACTORING_TRINOMIALS_CALCULATOR_STRINGS } from "@/components/tools/factoring-trinomials-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { FactorResult, FactorStep } from "@/lib/tool-kit/algebra/factor";
import type { TextOptions } from "@/lib/tool-kit/algebra/factor-text";
import {
  formatUni,
  substitutionText,
  type Uni,
  uniCoefficient,
  uniTrim,
} from "@/lib/tool-kit/algebra/uni";
import {
  formatPolynomial,
  formatStyled,
  RATIONAL_ZERO,
  signR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof FACTORING_TRINOMIALS_CALCULATOR_STRINGS;

interface Ctx extends TextOptions {
  strings: Strings;
}

const poly = (p: Uni, c: Ctx) => formatUni(p, c.variable, c.style, c.mark);
const wrap = (p: Uni, c: Ctx) => `(${poly(p, c)})`;
const wrapIfSum = (p: Uni, c: Ctx) =>
  p.filter((v) => v.num !== BigInt(0)).length > 1 ? wrap(p, c) : poly(p, c);
const squareOf = (text: string) =>
  text.length === 1 ? `${text}²` : `(${text})²`;
const cubeOf = (text: string) =>
  text.length === 1 ? `${text}³` : `(${text})³`;
const bracketNegative = (text: string) =>
  text.startsWith("−") ? `(${text})` : text;
const int = (n: bigint, c: Ctx) =>
  formatStyled({ num: n, den: BigInt(1) }, c.style, c.mark);

function acRows(
  step: Extract<FactorStep, { kind: "ac" }>,
  c: Ctx
): WorkedStep[] {
  const { strings } = c;
  const a = uniCoefficient(step.poly, 2);
  const b = uniCoefficient(step.poly, 1);
  const cc = uniCoefficient(step.poly, 0);
  const second = step.binomial;
  const other: Uni = [
    { num: step.g2, den: BigInt(1) },
    { num: step.g1, den: BigInt(1) },
  ];
  if (a.num === BigInt(1)) {
    return [
      {
        text: t(strings.simpleTrinomial, {
          c: formatStyled(cc, c.style, c.mark),
          b: formatStyled(b, c.style, c.mark),
          m: int(step.m, c),
          n: int(step.n, c),
        }),
        math: `${poly(step.poly, c)} = ${wrap(second, c)}${wrap(other, c)}`,
      },
    ];
  }
  const split = formatPolynomial(
    [
      { coefficient: a, variable: `${c.variable}²` },
      { coefficient: { num: step.m, den: BigInt(1) }, variable: c.variable },
      { coefficient: { num: step.n, den: BigInt(1) }, variable: c.variable },
      { coefficient: cc, variable: "" },
    ],
    c.style,
    c.mark
  );
  const g1 =
    step.g1 === BigInt(1) ? c.variable : `${int(step.g1, c)}${c.variable}`;
  const g2Size = step.g2 < BigInt(0) ? -step.g2 : step.g2;
  const grouped = `${g1}${wrap(second, c)} ${step.g2 < BigInt(0) ? "−" : "+"} ${int(g2Size, c)}${wrap(second, c)}`;
  return [
    {
      text: t(strings.acPair, {
        a: formatStyled(a, c.style, c.mark),
        c: bracketNegative(formatStyled(cc, c.style, c.mark)),
        product: int(step.product, c),
        b: formatStyled(b, c.style, c.mark),
        m: int(step.m, c),
        n: int(step.n, c),
      }),
    },
    { text: strings.acSplit, math: `${poly(step.poly, c)} = ${split}` },
    { text: strings.acGroup, math: grouped },
    { text: strings.acFactor, math: `${wrap(second, c)}${wrap(other, c)}` },
  ];
}

function groupingRows(
  step: Extract<FactorStep, { kind: "grouping" }>,
  c: Ctx
): WorkedStep[] {
  const { strings } = c;
  const p = step.poly;
  const high = uniTrim([
    RATIONAL_ZERO,
    RATIONAL_ZERO,
    uniCoefficient(p, 2),
    uniCoefficient(p, 3),
  ]);
  const low = uniTrim([uniCoefficient(p, 0), uniCoefficient(p, 1)]);
  const negative = signR(step.g2[0] ?? RATIONAL_ZERO) < 0;
  const g2 = negative
    ? step.g2.map((v) => ({ num: -v.num, den: v.den }))
    : step.g2;
  const bracket = wrap(step.binomial, c);
  return [
    {
      text: strings.groupPairs,
      math: `(${poly(high, c)}) + (${poly(low, c)}) = ${poly(step.g1, c)}${bracket} ${negative ? "−" : "+"} ${poly(g2, c)}${bracket}`,
    },
    {
      text: t(strings.groupCommon, { binomial: bracket }),
      math: `${bracket}${wrap(step.other, c)}`,
    },
  ];
}

function rowsFor(step: FactorStep, c: Ctx): WorkedStep[] {
  const { strings } = c;
  switch (step.kind) {
    case "gcf": {
      const raw = poly(step.gcf, c);
      const shown = raw.includes("/") ? `(${raw})` : raw;
      return [
        {
          text: t(step.negative ? strings.gcfNegative : strings.gcf, {
            gcf: shown,
          }),
          math: `${poly(step.poly, c)} = ${shown}${wrapIfSum(step.remaining, c)}`,
        },
      ];
    }
    case "difference-squares": {
      const second = { num: step.second, den: BigInt(1) };
      return [
        {
          text: t(strings.differenceSquares, {
            a: poly(step.first, c),
            b: int(step.second, c),
          }),
          math: `${poly(step.poly, c)} = ${squareOf(poly(step.first, c))} − ${squareOf(formatStyled(second, c.style, c.mark))} = (${poly(step.first, c)} − ${formatStyled(second, c.style, c.mark)})(${poly(step.first, c)} + ${formatStyled(second, c.style, c.mark)})`,
        },
      ];
    }
    case "perfect-square":
      return [
        {
          text: strings.perfectSquare,
          math: `${poly(step.poly, c)} = ${wrap(step.root, c)}²`,
        },
      ];
    case "sum-cubes":
    case "difference-cubes": {
      const sum = step.kind === "sum-cubes";
      return [
        {
          text: t(sum ? strings.sumCubes : strings.differenceCubes, {
            a: poly(step.first, c),
            b: int(step.second, c),
          }),
          math: `${poly(step.poly, c)} = ${cubeOf(poly(step.first, c))} ${sum ? "+" : "−"} ${cubeOf(int(step.second, c))} = (${poly(step.first, c)} ${sum ? "+" : "−"} ${int(step.second, c)})${wrap(step.trinomial, c)}`,
        },
      ];
    }
    case "ac":
      return acRows(step, c);
    case "grouping":
      return groupingRows(step, c);
    case "rational-root": {
      const root = formatStyled(step.root, c.style, c.mark);
      return [
        {
          text: t(strings.rationalRoot, { root, variable: c.variable }),
          math: `P(${root}) = ${substitutionText(step.poly, step.root, c.style, c.mark)} = 0`,
        },
        {
          text: t(strings.divideOut, { factor: wrap(step.factor, c) }),
          math: `${poly(step.poly, c)} = ${wrap(step.factor, c)}${wrap(step.quotient, c)}`,
        },
      ];
    }
    case "quadratic-pair":
      return [
        {
          text: strings.quadraticPair,
          math: `${poly(step.poly, c)} = ${wrap(step.first, c)}${wrap(step.second, c)}`,
        },
      ];
    default:
      return [{ text: irreducibleText(step, c), math: poly(step.poly, c) }];
  }
}

function irreducibleText(
  step: Extract<FactorStep, { kind: "irreducible" }>,
  c: Ctx
): string {
  const { strings } = c;
  const degree = step.poly.length - 1;
  if (degree === 2) {
    const a = uniCoefficient(step.poly, 2);
    const b = uniCoefficient(step.poly, 1);
    const cc = uniCoefficient(step.poly, 0);
    const disc = b.num * b.num - BigInt(4) * a.num * cc.num;
    return t(
      disc < BigInt(0)
        ? strings.primeQuadraticNegative
        : strings.primeQuadratic,
      {
        discriminant: int(disc, c),
      }
    );
  }
  return step.certain ? strings.primeCubic : t(strings.noSplit, { degree });
}

export function FactorStepList({
  result,
  ctx,
}: {
  result: FactorResult;
  ctx: Ctx;
}) {
  const rows = result.steps.flatMap((step) => rowsFor(step, ctx));
  return <StepList steps={rows} />;
}
